import { useEffect, useRef } from 'react';
import { useStore } from 'react-redux';

import SLICK_LOGO from 'assets/images/Slick-Logo2-0_Live_Positive.svg';
import CONF from 'config/conf.singleton';
import ENV from 'config/env.conf';
import BRAND_COLOR from 'constants/brandColors.const';
import MS from 'constants/time/ms.const';
import { selectLockToken } from 'selectors/simple';
import { selectMainToken } from 'utils/cognito.util';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import { isEnvProd } from 'utils/env/determine.util';
import logger from 'utils/logger.util';

const L = logger('StripeCapitalDialog');

const SDK_SCRIPT_SELECTOR = 'script[data-daysmart-payments-sdk="true"]';
const SDK_GLOBAL = 'DaySmartPaymentsSDK';
const SDK_COMPONENT_NAME = 'capital-offer-dialog';
const DEFAULT_SDK_URL = 'https://assets.daysmartpayments.com/payments-sdk/v0.1/client.iife.js';

const SESSION_FALLBACK_MS = MS.hour;
const SECONDS_VS_MS_THRESHOLD = 1e12;

const HTTP_NO_CONTENT = 204;
const HTTP_NOT_FOUND = 404;

const CAPITAL_ERROR = {
  offerDismissed: 'offer-dismissed',
  noOffers: 'no-offers',
  sdkInitFailed: 'sdk-init-failed',
  sdkLoadFailed: 'sdk-load-failed',
  componentCreateFailed: 'component-create-failed',
};

let sdkPromise;

const getSessionUrl = (salonId) => {
  const envUrl = fromAnyToTrimmed(ENV.REACT_APP_DAYSMART_CAPITAL_SESSION_URL);
  if (envUrl) return envUrl;

  const root = (CONF.apiRoot || '').replace(/\/+$/, '');
  return `${root}/payments/capital/${salonId}/session/`;
};

const getSdkUrl = () => fromAnyToTrimmed(ENV.REACT_APP_DAYSMART_PAYMENTS_SDK_URL) || DEFAULT_SDK_URL;

const removeNode = (node) => {
  if (node?.parentNode) {
    node.parentNode.removeChild(node);
  }
};

const toExpiresAtMs = (expiresAt) => {
  if (typeof expiresAt === 'number') {
    return expiresAt < SECONDS_VS_MS_THRESHOLD ? expiresAt * 1000 : expiresAt;
  }
  if (typeof expiresAt === 'string') {
    const parsed = Date.parse(expiresAt);
    if (parsed) return parsed;
  }
  return Date.now() + SESSION_FALLBACK_MS;
};

const normalizeSessionPayload = (payload) => {
  if (!payload?.token) return payload;
  return { ...payload, expiresAt: toExpiresAtMs(payload.expiresAt) };
};

const fetchClientToken = async (requestPayload, { salonId, getAuthToken, getLockToken }) => {
  const url = getSessionUrl(salonId);
  L.debug('fetchClientToken()', 'url:', url);

  const headers = { 'Content-Type': 'application/json' };
  const token = getAuthToken?.();
  if (token) headers.Authorization = `Bearer ${token}`;
  const lt = getLockToken?.();
  if (lt) headers.LOCK_TOKEN = lt;

  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(requestPayload),
  });

  if (response.status === HTTP_NO_CONTENT) {
    throw new Error(CAPITAL_ERROR.offerDismissed);
  }

  if (response.status === HTTP_NOT_FOUND) {
    throw new Error(CAPITAL_ERROR.noOffers);
  }

  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(fromAnyToTrimmed(payload?.error) || '');
  }

  return normalizeSessionPayload(payload);
};

const loadSdk = (sdkUrl) => {
  const existingSdk = window?.[SDK_GLOBAL];
  if (existingSdk?.initializeClientSDK) {
    return Promise.resolve(existingSdk);
  }

  if (sdkPromise) return sdkPromise;

  sdkPromise = new Promise((resolve, reject) => {
    const handleLoad = () => {
      const loaded = window?.[SDK_GLOBAL];
      if (loaded?.initializeClientSDK) {
        resolve(loaded);
        return;
      }
      reject(new Error(CAPITAL_ERROR.sdkInitFailed));
    };

    const handleError = () => reject(new Error(CAPITAL_ERROR.sdkLoadFailed));

    const existingScript = document.querySelector(SDK_SCRIPT_SELECTOR);
    if (existingScript) {
      handleLoad();
      if (window?.[SDK_GLOBAL]?.initializeClientSDK) return;

      existingScript.addEventListener('load', handleLoad, { once: true });
      existingScript.addEventListener('error', handleError, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = sdkUrl;
    script.async = true;
    script.dataset.daysmartPaymentsSdk = 'true';
    script.addEventListener('load', handleLoad, { once: true });
    script.addEventListener('error', handleError, { once: true });
    document.body.appendChild(script);
  }).catch((error) => {
    sdkPromise = null;
    throw error;
  });

  return sdkPromise;
};

const DIALOG_APPEARANCE = {
  programName: 'Stripe Financing Offers',
  programLogoUrl: SLICK_LOGO,
  headingColor: BRAND_COLOR.violet,
  primaryButtonColor: BRAND_COLOR.violet,
  priceGradientStartColor: BRAND_COLOR.gradientDark,
  priceGradientEndColor: BRAND_COLOR.gradientLight,
};

const DIALOG_DELAY = 7;
const DIALOG_OCCURRENCES = 5;

const buildDialogOptions = (merchantId) => {
  const options = { delay: DIALOG_DELAY, occurrences: DIALOG_OCCURRENCES };
  if (merchantId) options.thirdPartyMerchantId = merchantId;
  return options;
};

const isEmptyPayload = (value) => !value || typeof value !== 'object' || !Object.keys(value).length || value.customerId == null || value.customerId === '';

const hasValidPayload = (payloads) => Array.isArray(payloads) && payloads.some((p) => !isEmptyPayload(p));

const fetchFirstAvailableToken = async (payloads, cancelled, authCtx) => {
  for (let i = 0; i < payloads.length; i++) {
    const payload = payloads[i];
    if (isEmptyPayload(payload)) continue;

    try {
      const token = await fetchClientToken(payload, authCtx);
      if (cancelled()) return null;
      return { token, payload };
    } catch (error) {
      if (error?.message === CAPITAL_ERROR.noOffers && i < payloads.length - 1) {
        L.debug('fetchFirstAvailableToken()', 'no offers for payload', i, '– trying next');
        continue;
      }
      throw error;
    }
  }
  throw new Error(CAPITAL_ERROR.noOffers);
};

export const StripeCapitalDialog = ({ launchKey, salonId, merchantId, requestPayloads, onError, errorMessages }) => {
  const activeDialogRef = useRef(null);
  const store = useStore();

  useEffect(() => {
    if (!launchKey || !salonId) return;

    let cancelled = false;
    const isCancelled = () => cancelled;

    const getAuthToken = () => selectMainToken(store.getState());
    const getLockToken = () => selectLockToken(store.getState());
    const authCtx = { salonId, getAuthToken, getLockToken };

    const launch = async () => {
      if (!hasValidPayload(requestPayloads)) {
        onError?.(errorMessages?.missingId || '');
        return;
      }

      onError?.('');

      try {
        removeNode(activeDialogRef.current);
        activeDialogRef.current = null;

        const sdk = await loadSdk(getSdkUrl());
        if (cancelled) return;

        const { initializeClientSDK } = sdk;
        if (typeof initializeClientSDK !== 'function') {
          throw new Error(CAPITAL_ERROR.sdkInitFailed);
        }

        const result = await fetchFirstAvailableToken(requestPayloads, isCancelled, authCtx);
        if (cancelled || !result) return;

        let prefetched = result.token;
        const winningPayload = result.payload;

        const client = initializeClientSDK({
          fetchClientToken: async () => {
            if (prefetched) {
              const cached = prefetched;
              prefetched = null;
              return cached;
            }
            return fetchClientToken(winningPayload, authCtx);
          },
          uat: !isEnvProd(),
        });

        const dialog = client.createComponent(SDK_COMPONENT_NAME, {
          options: buildDialogOptions(merchantId),
          appearance: DIALOG_APPEARANCE,
        });
        if (!dialog) {
          throw new Error(CAPITAL_ERROR.componentCreateFailed);
        }

        document.body.appendChild(dialog);
        activeDialogRef.current = dialog;
        L.debug('launch()', 'dialog appended to body');
      } catch (error) {
        L.warn('launch()', error);
        if (!cancelled) {
          if (error?.message === CAPITAL_ERROR.offerDismissed) {
            onError?.(errorMessages?.dismissed || '');
          } else if (error?.message === CAPITAL_ERROR.noOffers) {
            onError?.(errorMessages?.noOffers || '');
          } else {
            onError?.(errorMessages?.default || error?.message || '');
          }
        }
      }
    };

    void launch();

    return () => {
      cancelled = true;
    };
  }, [launchKey, salonId, merchantId, requestPayloads, onError, errorMessages, store]);

  useEffect(
    () => () => {
      removeNode(activeDialogRef.current);
      activeDialogRef.current = null;
    },
    [],
  );

  return null;
};
